// Global Instructions Rule Applied!

import React, { useMemo, useRef } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import Button from '../Button.jsx'
import { KanbanBoardProps, KanbanItem } from './interfaces.ts'
import { AutoScrollMonitor, KanbanColumn } from './components/index.ts'
import { filterVisibleColumns, groupItemsIntoColumns } from './controller/index.ts'

export function KanbanBoard<T extends KanbanItem>({
  columns,
  items,
  cardConfig,
  columnHeaderConfig,
  onMoveItem,
  onUpdateItem,
  loading = false,
  emptyState,
  className = '',
  scrollContainerRef,
  getColumnId,
  successfullyMovedCardId,
  sameColumnOnly = false
}: KanbanBoardProps<T>): React.ReactElement {
  const internalScrollRef = useRef<HTMLDivElement>(null)
  const scrollRef = scrollContainerRef || internalScrollRef

  // Generate columns from items
  const columnData = useMemo(() => {
    const visibleColumns = filterVisibleColumns(columns)
    const getColumnIdFn =
      getColumnId ||
      ((item: T) => {
        // Fallback: assume item has a columnId property
        return (item as T & { columnId: string }).columnId || ''
      })
    return groupItemsIntoColumns(visibleColumns, items, getColumnIdFn)
  }, [columns, items, getColumnId])

  const handleMoveItem = async (
    itemId: string,
    fromColumnId: string,
    toColumnId: string,
    newIndex: number
  ): Promise<void> => {
    await onMoveItem(itemId, fromColumnId, toColumnId, newIndex)
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <AutoScrollMonitor scrollContainerRef={scrollRef} />
      <div className={`h-full ${className}`}>
        <div className='w-full h-full flex flex-col bg-white pl-2 pr-4 pb-4'>
          {loading ? (
            <div className='flex-1 flex items-center justify-center'>
              <div className='text-center'>
                <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4'></div>
                <p className='text-gray-600'>Loading...</p>
              </div>
            </div>
          ) : columnData.length === 0 ? (
            <div className='flex-1 flex items-center justify-center'>
              <div className='text-center text-gray-600'>
                <p>{emptyState?.title || 'No columns found'}</p>
                {emptyState?.action || (
                  <Button variant='primary' className='mt-4'>
                    Reload
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div ref={scrollRef} className='flex-1 overflow-x-auto pl-2 pr-4 pb-4'>
              <div
                className='grid gap-4 min-w-[1800px]'
                style={{ gridTemplateColumns: `repeat(${columnData.length}, minmax(300px, 1fr))` }}
              >
                {columnData.map((column) => (
                  <KanbanColumn
                    key={column.column.id}
                    column={column.column}
                    items={column.items}
                    cardConfig={cardConfig}
                    columnHeaderConfig={columnHeaderConfig}
                    onMoveItem={handleMoveItem}
                    onUpdateItem={onUpdateItem}
                    successfullyMovedCardId={successfullyMovedCardId}
                    sameColumnOnly={sameColumnOnly}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </DndProvider>
  )
}
